// Biggest prize pools — animated leaderboard. Ranks open markets by
// `prizePool` (D6 USDC) descending and shows the top N at once. A spotlight
// cycles through the rows every PRIZE_POOLS_SPOTLIGHT_MS; the active pool
// expands into an image-backed hero while the rest stay compact one-liners.
// The expand/collapse + image cross-fade are pure CSS transitions for smooth
// 60fps motion.
//
// Prize amounts are realtime: `useGetMarkets` carries the `add: { prizePool }`
// trade/boost NATS recipes. But a list hook's per-market subjects are
// wildcard dispatch-trie entries only — a market's bounded trade/boost channel
// (`morfi.market.<pubkey>.user.*.{trade,boost}.v0`) only reaches the wire once a
// rendered row registers it. So each leaderboard row attaches
// `query.itemObserver(pubkey)` to subscribe that market's channels; without it
// the prize pool freezes at first paint. `useLiveListData` then re-renders the
// parent on each patch so the leaderboard re-ranks and reorders live.
// Self-hides when no row has `prizePool > 0`.

'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetMarketsRow, useGetMarkets } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { type JSX, useEffect, useRef } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { Icon } from '@/components/ui/icon';
import { Award01Icon } from '@/components/ui/icon/glyphs';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { MARKETS_LIST_OPEN_STATUS } from '@/lib/market/listing-status';
import { cn } from '@/lib/ui/utils';
import { PrizePoolsCardSkeleton } from './prize-pools-card-skeleton';
import { RIGHT_COLUMN_CARD_PRIZE_SHELL } from './right-column-card-chrome';
import { TopMarketsByPrizeRow } from './top-markets-by-prize-row';
import { PRIZE_POOLS_SPOTLIGHT_MS, useHotTakesCarousel } from './use-hot-takes-carousel';
const DEFAULT_LIMIT = 5 as const;
const MIN_LIMIT = 3 as const;
const MAX_LIMIT = 5 as const;

// Dwell time before a hovered (non-spotlighted) pool animates open and takes
// over the spotlight. It then holds until the pointer leaves the widget, at
// which point the section-level resume() lets auto-cycling pick back up.
const HOVER_EXPAND_DELAY_MS = 1_000 as const;
interface RankedMarket {
  pubkey: string;
  title: string;
  prizePool: string;
  coverImageUrl: string;
  coverImageNsfw: boolean;
  category: string;
}
type PrizeRow = Pick<GetMarketsRow, 'pubkey' | 'title' | 'prizePool' | 'coverImageUrl' | 'coverImageNsfw' | 'category'>;
function isPositivePrizePool(prizePool: string): boolean {
  if (!/^\d+$/.test(prizePool)) return false;
  return BigInt(prizePool) > BigInt(0);
}
function rankByPrize(markets: readonly PrizeRow[], limit: 3 | 4 | 5): RankedMarket[] {
  const filtered = markets.filter(mkt => isPositivePrizePool(mkt.prizePool));
  const sorted = filtered.toSorted((a, b) => {
    const ap = BigInt(a.prizePool);
    const bp = BigInt(b.prizePool);
    if (ap < bp) return 1;
    if (ap > bp) return -1;
    return 0;
  });
  return sorted.slice(0, limit).map(mkt => ({
    pubkey: mkt.pubkey,
    title: mkt.title,
    prizePool: mkt.prizePool,
    coverImageUrl: mkt.coverImageUrl ?? '',
    coverImageNsfw: mkt.coverImageNsfw === true,
    category: mkt.category ?? ''
  }));
}
export interface TopMarketsByPrizeWidgetProps {
  limit?: 3 | 4 | 5;
  className?: string;
}
export function TopMarketsByPrizeWidget(t0) {
  const $ = _c(19);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    limit: t2,
    className
  } = t1;
  const limit = t2 === undefined ? DEFAULT_LIMIT : t2;
  const t = useTranslations("markets.top_markets_by_prize");
  const rawLimit = limit;
  const clamped = Math.max(MIN_LIMIT, Math.min(MAX_LIMIT, rawLimit)) as 3 | 4 | 5;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      sortBy: "prizePool",
      sortOrder: "desc",
      status: MARKETS_LIST_OPEN_STATUS
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const query = useGetMarkets(t3);
  const liveRows = useLiveListData(query.queryKey, query.rows, query.entityQueryKey);
  const ranked = rankByPrize(liveRows, clamped);
  const slideCount = ranked.length;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      autoAdvanceMs: PRIZE_POOLS_SPOTLIGHT_MS
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const {
    activeIndex,
    goTo,
    pause,
    resume
  } = useHotTakesCarousel(slideCount, t4);
  const hoverTimerRef = useRef(null);
  const clearHoverTimer = () => {
    if (hoverTimerRef.current !== null) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };
  const armHoverExpand = index => {
    clearHoverTimer();
    if (index === activeIndex) {
      return;
    }
    hoverTimerRef.current = setTimeout(() => {
      goTo(index);
    }, HOVER_EXPAND_DELAY_MS);
  };
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  useEffect(() => () => {
    if (hoverTimerRef.current !== null) {
      clearTimeout(hoverTimerRef.current);
    }
  }, t5);
  if (query.isPending) {
    let t6;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <PrizePoolsCardSkeleton testId="top-markets-by-prize-widget-loading" />;
      $[5] = t6;
    } else {
      t6 = $[5];
    }
    return t6;
  }
  if (slideCount === 0) {
    return null;
  }
  const safeActiveIndex = Math.min(activeIndex, slideCount - 1);
  let t6;
  if ($[6] !== className) {
    t6 = cn("flex min-h-0 flex-1 flex-col", className);
    $[6] = className;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== safeActiveIndex) {
    t7 = (entry, index_0) => entry.coverImageUrl.length > 0 ? <div key={entry.pubkey} className={cn("absolute inset-0 transition-opacity duration-[600ms] ease-default motion-reduce:transition-none", index_0 === safeActiveIndex ? "opacity-[0.47] dark:opacity-[0.48]" : "opacity-0")}><div className="absolute inset-0 scale-105 blur-[3.5px] saturate-[1.4] contrast-[1.08]"><OptimizedImage variant="hero" entity={{
          kind: "market",
          pubkey: entry.pubkey,
          category: entry.category.toLowerCase(),
          coverImageUrl: entry.coverImageUrl,
          nsfw: entry.coverImageNsfw
        }} alt="" sizes="360px" className="size-full object-cover object-right" priority={index_0 === 0} /></div></div> : null;
    $[8] = safeActiveIndex;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="absolute inset-0 bg-gradient-to-r from-background-primary via-background-primary/49 via-[56%] to-background-primary/28 to-[85%]" />;
    t9 = <div className="absolute inset-0 bg-gradient-to-t from-background-primary/23 to-transparent to-[55%]" />;
    $[10] = t8;
    $[11] = t9;
  } else {
    t8 = $[10];
    t9 = $[11];
  }
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Icon icon={Award01Icon} aria-hidden="true" className="size-3.5 shrink-0 text-outcome-amber-cta dark:text-outcome-amber-text" />;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t) {
    t11 = t("heading");
    $[13] = t;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t11) {
    t12 = <div className="relative z-10 flex items-center gap-1.5 px-3.5 pt-3 pb-2" data-testid="top-markets-by-prize-header">{t10}<h2 className="text-caption font-semibold uppercase tracking-wide text-foreground-tertiary">{t11}</h2></div>;
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const t13 = "relative z-10 flex min-h-0 flex-1 flex-col pb-2";
  const t14 = "top-markets-by-prize-list";
  const t15 = ranked.map((entry_0, index_1) => {
    const isActive = index_1 === safeActiveIndex;
    return <div key={entry_0.pubkey} ref={query.itemObserver(entry_0.pubkey)} onPointerEnter={() => armHoverExpand(index_1)} onPointerLeave={clearHoverTimer} className={cn("relative flex min-h-0 basis-0 transition-[flex-grow] duration-[800ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none", isActive ? "grow-[3.25]" : "grow")}><TopMarketsByPrizeRow marketPubkey={entry_0.pubkey} marketTitle={entry_0.title} prizePool={entry_0.prizePool} rank={index_1 + 1} isActive={isActive} showSeparator={index_1 > 0} /></div>;
  });
  let t16;
  if ($[17] !== t15) {
    t16 = <div className={t13} data-testid={t14}>{t15}</div>;
    $[17] = t15;
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  return <section className={t6} data-testid="top-markets-by-prize-widget" onPointerEnter={pause} onPointerLeave={resume} onFocusCapture={pause} onBlurCapture={resume}><div className={RIGHT_COLUMN_CARD_PRIZE_SHELL}><div aria-hidden="true" data-testid="top-markets-by-prize-cover" className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-card">{ranked.map(t7)}{t8}{t9}</div>{t12}{t16}</div></section>;
}